<template>
	<div class="integration-details">
		<div class="header">
			<DetailsHeader
				:logoUrl="integrationObject?.icon_url"
				:title="integrationObject?.display_name"
			>
				<template #right-controls>
					<Button
							v-if="connectionId"
							class="delete-connection-btn"
							@click="showDeleteModal = true"
							link
							:icon="TrashCanIcon"
							:iconProps="{ type: 'regular' }"
						>
							{{ t('admin_integrations_delete_connection') }}
						</Button>
				</template>
			</DetailsHeader>
		</div>

		<div class="content">
			<component 
				:is="configurationComponent"
				:integration-id="integrationId"
				:integration-object="integrationObject"
				:connection-id="connectionId"
				:platform="platform"
			/>
		</div>		
		<DeleteModal
			v-if="showDeleteModal"
			@close="showDeleteModal = false"
			@delete="deleteConnection"
			:title="t('admin_integrations_delete_connection')"
			:showConfirm="true"
			:isProcessing="isDeleting"
			:bodyText="t('admin_integrations_delete_modal_body')"
			data-cy="deleteModal"
			:checkboxText="t('admin_integrations_delete_modal_confirm')"
		/>
	</div>
</template>

<script setup>
import { computed, ref } from 'vue'
import DetailsHeader from './DetailsHeader'
import { Button } from '@/components/common'
import { TrashCanIcon } from '@/icons'
import { useI18n } from 'vue-i18n'
import DeleteModal from '@/components/DeleteModal'
import { useRouter } from 'vue-router'

// Import specific configuration components
import LTIConfiguration from './Configurations/LTIConfiguration'
import GoogleWorkspaceConfiguration from './Configurations/GoogleWorkspaceConfiguration'
import { integrationDelete } from '@/api/integrations'

// Props
const props = defineProps({
	integrationId: { type: [String, Number], required: true },
	integrationObject: { type: Object, required: false },
	connectionId: { type: [String, Number], required: false, default: null },
	platform: { type: String, required: false, default: null }
})

// Reactive State
const { t } = useI18n()
const router = useRouter()
const showDeleteModal = ref(false)
const isDeleting = ref(false)

// Methods
const deleteConnection = async () => {
	isDeleting.value = true
	try {
		await integrationDelete(`/connections/${props.connectionId}`)
		isDeleting.value = false
		router.push({ name: 'integrations', params: { type: 'CONNECTED' } })
	} catch (e) {
		console.error(e)
		isDeleting.value = false
		alert.error(t('common_api_error_save'))
	}
}

// Computed
const configurationComponents = {
	'lti13': LTIConfiguration,
	'google_workspace': GoogleWorkspaceConfiguration,
}

const configurationComponent = computed(() => {
	const integrationType = props.integrationObject?.type || 
						   props.integrationObject?.key || 
						   'default'
	
	return configurationComponents[integrationType] || DefaultConfiguration
})

</script>

<style lang="scss" scoped>
@import 'src/sass/variables';
.integration-details {
	.content {
		height: calc(100vh - 125px);
		display: flex;
		align-items: center;
		flex-direction: column;
        padding: 25px 144px;
        overflow-y: auto;
	}
	.delete-connection-btn {
		color: var(--red-400) !important;
		&:hover {
			color: var(--red-300) !important;
		}
	}
}
</style>
